import React, { useState } from 'react';
import AddChild from 'pages/Personal/components/AddChild/AddChild';
import { useTranslation } from 'react-i18next';
import useChildren from 'src/hooks/useChildren';
import EditIcon from 'pages/Personal/components/ChildrenList/EditIcon/EditIcon';
import DeleteIcon from 'pages/Personal/components/ChildrenList/DeleteIcon/DeleteIcon';
import Dialog from 'pages/Personal/components/Dialog/Dialog';
import { Child } from 'pages/Personal/components/AddChild/AddChild.types';
import useBookedLessons from 'src/hooks/useBookedLessons';
import DeleteConfirmation from 'pages/Personal/components/DeleteConfirmation/DeleteConfirmation';
import { format } from 'date-fns';

const ChildrenList: React.FC = (): React.ReactElement => {
	const [isAddChildMode, setIsAddChildMode] = useState<boolean>(false);
	const [isDeleteChildMode, setIsDeleteChildMode] = useState<boolean>(false);
	const [isAddingChild, setIsAddingChild] = useState<boolean>(false);
	const { bookedLessons } = useBookedLessons({ lessonType: 'present' });
	const [reFetch, setReFetch] = useState<boolean>(true);
	const { children, deleteChild } = useChildren(reFetch);
	const { t } = useTranslation();
	const [childToEdit, setChildToEdit] = useState<Child>({});
	const [childToDelete, setChildToDelete] = useState<Child>({});

	const handleEditChild = (child: Child) => {
		setChildToEdit(child);
		setIsAddChildMode(true);
	};

	const getChildren = () => {
		return children ? <table className={'details-table'}>
			<thead>
			<tr>
				<th>{t('details-table.header.firstName')}</th>
				<th>{t('details-table.header.lastName')}</th>
				<th>{t('details-table.header.age')}</th>
				<th>{t('details-table.header.nextLessons')}</th>
				<th />
			</tr>
			</thead>
			<tbody>
			{children?.length > 0 ? children.map((child: any) => {
				return <tr key={child.id}>
					<td>{child.firstname}</td>
					<td>{child.lastname}</td>
					<td>{child.age}</td>
					<td>{getStudiedLessonsBiChild(child.id)}</td>
					<td className={'actions-column'}>
						<div>
							<div role={'button'} className={'transparent-button'} onClick={() => handleEditChild(child)}>
								<EditIcon/>
							</div>
							<div role={'button'} className={'transparent-button'} onClick={() => {
								setChildToDelete(child);
								setIsDeleteChildMode(true);
							}}>
								<DeleteIcon/>
							</div>
						</div>
					</td>
				</tr>;
			}) : <tr>
				<td colSpan={5} style={{ textAlign: 'center' }}>
					{t('details-table.no-registered-children')}
				</td>
			</tr>}
			</tbody>
		</table> : null;
	};

	const getStudiedLessonsBiChild = (childId: number) => {
		const lessons: number[] = [];
		const children: number[] = [];
		const booked: any = [];
		bookedLessons.map((bLesson: any) => {
			if (
				lessons.indexOf(bLesson.lessonId) !== -1
				&& children.indexOf(bLesson.childId) !== -1
			) {
				return bLesson;
			}
			lessons.push(bLesson.lessonId);
			children.push(bLesson.childId);
			booked.push(bLesson);
			return bLesson;
		});
		const nextLesson = booked.filter((b: any) => b.childId === childId)[0];
		if (!nextLesson) {
			return null;
		}
		const date = new Date(nextLesson.bookingDate);
		return <div className={'lessons-container'}>
			<div className={'lesson green'}>
				{`${nextLesson.lessonName} (${format(date, 'dd/MM/yyyy')}, ${nextLesson.startTime.slice(0, -3)}-${nextLesson.endTime.slice(0, -3)})`}
			</div>
		</div>;
	};

	const handleDeleteChild = (childToDelete: Child) => {
		if (!childToDelete.id) {
			setIsDeleteChildMode(false);
			setChildToDelete({});
			return;
		}
		deleteChild(childToDelete.id).then(() => {
			setChildToDelete({});
			setIsDeleteChildMode(false);
			setReFetch(!reFetch);
		})
	}

	return <>
		<div className="you-study">
			<div className="courses">
				{getChildren()}
			</div>
			<div style={{
				width: '100%',
				display: 'flex',
				justifyContent: 'flex-end'
			}}>
				<button className={'active'}
								onClick={() => handleEditChild({})} disabled={isAddingChild}
				>{t('pages.personal.addChild')}</button>
			</div>
		</div>
		{isAddChildMode && (
			<Dialog onClose={() => setIsAddChildMode(false)}>
				<AddChild
					setReFetch={setReFetch}
					setIsAddChildMode={setIsAddChildMode}
					setIsAddingChild={setIsAddingChild}
					isAddingChild={isAddingChild}
					reFetch={reFetch}
					child={childToEdit}
				/>
			</Dialog>
		)}
		{isDeleteChildMode && (
			<Dialog onClose={() => setIsDeleteChildMode(false)}>
				<DeleteConfirmation
					acceptAction={() => handleDeleteChild(childToDelete)}
					cancelAction={() => setIsDeleteChildMode(false)}
					deleteEntity={`${childToDelete.firstname} ${childToDelete.lastname}`}
				/>
			</Dialog>
		)}
	</>;
};

export default ChildrenList;
